import type { FlightFare } from "../data/mock-flights";
import { nightsBetween, type RoomOption } from "../data/mock-hotels";

/** 1 point spends as ₹1. At most half the fare can be points; the rest is cash. */
export const POINT_VALUE_INR = 1;
export const MAX_POINTS_SHARE = 0.5;

export function maxRedeemablePoints(
  totalInr: number,
  availablePoints: number,
  pointValue = POINT_VALUE_INR,
) {
  const value = pointValue > 0 ? pointValue : 1;
  const cap = Math.floor((Math.max(0, totalInr) * MAX_POINTS_SHARE) / value + 1e-9);
  return Math.max(0, Math.min(Math.floor(availablePoints), cap));
}

export function clampPoints(
  requested: number,
  totalInr: number,
  availablePoints: number,
  pointValue = POINT_VALUE_INR,
) {
  const max = maxRedeemablePoints(totalInr, availablePoints, pointValue);
  return Math.max(
    0,
    Math.min(max, Math.floor(Number.isFinite(requested) ? requested : 0)),
  );
}

export type StayQuote = {
  nights: number;
  rooms: number;
  base: number;
  discount: number;
  taxes: number;
  total: number;
  pointsEarn: number;
  nightlyMember: number;
};

export function quoteStay(
  room: RoomOption,
  checkIn: string,
  checkOut: string,
  roomCount: number,
): StayQuote | null {
  const nights = nightsBetween(checkIn, checkOut);
  const rooms = Math.max(1, roomCount);
  if (nights < 1) return null;
  const units = nights * rooms;
  const base = room.pricePerNight * units;
  const discount = Math.round(base * (room.memberDiscountPct / 100));
  const taxes = room.taxesPerNight * units;
  const total = base - discount + taxes;
  return {
    nights,
    rooms,
    base,
    discount,
    taxes,
    total,
    pointsEarn: room.pointsEarn * units,
    nightlyMember: Math.round(
      room.pricePerNight * (1 - room.memberDiscountPct / 100),
    ),
  };
}

export type FlightQuote = {
  passengers: number;
  base: number;
  discount: number;
  total: number;
  pointsEarn: number;
};

export function quoteFlight(fare: FlightFare, passengers: number): FlightQuote {
  const pax = Math.max(1, passengers);
  const base = fare.price * pax;
  const discount = Math.round(base * (fare.memberDiscountPct / 100));
  return {
    passengers: pax,
    base,
    discount,
    total: base - discount,
    pointsEarn: fare.pointsEarn * pax,
  };
}
